import api, { errorMessage } from "../../lib/api";
import { Modal } from "../Modal";
import { ActionButton, GhostButton, FormField, inputClass } from "../Primitives";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { GuardRecentActivity } from "./OfficerRecentActivity";

/**
 * Full Add / Edit Officer form. Owns nothing except the promote/demote/reset
 * side-effect calls to the backend — everything else (form state, cert keys,
 * save handler) is lifted so the parent controls the lifecycle.
 */
export function GuardFormModal({
  editing, form, setForm,
  certKeys, setCertKeys,
  offices = [],
  onClose, onSave, onSuccessAndClose,
}) {
  const promoteDemote = async (action, msg) => {
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/guards/${editing.id}/${action}`);
      toast.success(action === "promote-supervisor" ? "Officer promoted to supervisor" : "Supervisor access revoked");
      onSuccessAndClose();
    } catch (e) {
      toast.error(errorMessage(e, action === "promote-supervisor" ? "Promote failed" : "Demote failed"));
    }
  };

  const forcePwReset = async () => {
    if (!window.confirm(`Force ${editing.name} to change their password on next login?`)) return;
    try {
      await api.post(`/guards/${editing.id}/force-password-reset`);
      toast.success("Password reset flag set");
    } catch (e) { toast.error(errorMessage(e, "Failed")); }
  };

  const updateCert = (idx, patch) => {
    const next = [...form.certifications];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, certifications: next });
  };

  const addCert = () => {
    const newKey = `c-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setCertKeys((k) => [...k, newKey]);
    setForm({ ...form, certifications: [...(form.certifications || []), { name: "", no_expiry: false, issued_at: "", expires_at: "" }] });
  };

  const removeCert = (idx) => {
    setCertKeys((k) => k.filter((_, i) => i !== idx));
    setForm({ ...form, certifications: form.certifications.filter((_, i) => i !== idx) });
  };

  return (
    <Modal onClose={onClose} title={editing ? "Edit Officer" : "Add Officer"} testId="guard-form-modal">
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Full Name">
          <input data-testid="guard-name-input" className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </FormField>
        <FormField label="Badge No.">
          <input data-testid="guard-badge-input" className={inputClass} value={form.badge_no} onChange={(e) => setForm({ ...form, badge_no: e.target.value })} />
        </FormField>
        <FormField label="Phone *">
          <input data-testid="guard-phone-input" required type="tel" className={inputClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </FormField>
        <FormField label="Email *">
          <input data-testid="guard-email-input" required type="email" className={inputClass} value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </FormField>
        <FormField label="Job Role">
          <select data-testid="guard-role-select" className={inputClass} value={form.role || "guard"} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="guard">Officer</option>
            <option value="supervisor">Supervisor</option>
            <option value="dispatcher">Dispatcher</option>
          </select>
        </FormField>
        <FormField label="Hire Date">
          <input type="date" className={inputClass} value={form.hire_date || ""} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} />
        </FormField>
        <FormField label="Hourly Rate (USD)">
          <input type="number" min="0" step="0.5" className={inputClass} value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} />
        </FormField>
        <FormField label="Max Weekly Hours (blank = unlimited)">
          <input data-testid="guard-max-hours-input" type="number" min="0" max="168" placeholder="Any" className={inputClass} value={form.max_weekly_hours} onChange={(e) => setForm({ ...form, max_weekly_hours: e.target.value })} />
        </FormField>
        <FormField label="Status">
          <select className={inputClass} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="active">Active</option>
            <option value="on_leave">On Leave</option>
            <option value="inactive">Inactive</option>
          </select>
        </FormField>
        <FormField label="Agency Office">
          <select
            data-testid="guard-office-select"
            className={inputClass}
            value={form.office_id || ""}
            onChange={(e) => setForm({ ...form, office_id: e.target.value || null })}
          >
            <option value="">— unassigned —</option>
            {offices.map((o) => (
              <option key={o.id} value={o.id}>{o.name}{o.is_active ? "" : " (inactive)"}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Deactivation Date (blocks shifts on/after)">
          <input data-testid="guard-deactivation-input" type="date" className={inputClass} value={form.deactivation_date || ""} onChange={(e) => setForm({ ...form, deactivation_date: e.target.value })} />
        </FormField>
        <FormField label="Reactivation Date (re-allows shifts from)">
          <input data-testid="guard-reactivation-input" type="date" className={inputClass} value={form.reactivation_date || ""} min={form.deactivation_date || undefined} onChange={(e) => setForm({ ...form, reactivation_date: e.target.value })} />
        </FormField>

        <div className="col-span-2 border-t border-[#27272A] pt-4">
          <div className="label-mute mb-2">Login Credentials (optional)</div>
          <div className="text-[11px] text-zinc-500 mb-3 leading-snug">
            Fill in to create a login for this {form.role === "supervisor" || form.role === "dispatcher" ? "supervisor" : "guard"}. They will use these credentials to clock in / out and (if supervisor/dispatcher) access operational pages.
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Username (email)">
              <input data-testid="guard-username-input" type="email" className={inputClass} value={form.username || ""} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder={form.email ? form.email : "user@example.com"} />
            </FormField>
            <FormField label={editing ? "New Password (blank = unchanged)" : "Password (min 8 chars)"}>
              <input data-testid="guard-password-input" type="text" className={inputClass} value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />
            </FormField>
          </div>
          {editing && editing.user_id && (
            <div className="text-[10px] text-[#34C759] uppercase tracking-widest mt-2" data-testid="guard-login-status">
              ✓ Login linked · username {editing.username}
            </div>
          )}
          {editing && editing.user_id && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="guard-admin-actions">
              {editing.is_supervisor ? (
                <GhostButton
                  type="button"
                  data-testid="demote-supervisor-btn"
                  onClick={() => promoteDemote("demote-supervisor", "Revoke supervisor access from this guard? They will still be schedulable as a guard.")}
                >
                  Revoke Supervisor
                </GhostButton>
              ) : (
                <GhostButton
                  type="button"
                  data-testid="promote-supervisor-btn"
                  onClick={() => promoteDemote("promote-supervisor", `Grant supervisor access to ${editing.name}? They will keep being scheduled as a guard AND gain access to supervisor features.`)}
                >
                  Make Supervisor
                </GhostButton>
              )}
              <GhostButton
                type="button"
                data-testid="force-password-reset-btn"
                onClick={forcePwReset}
              >
                Force Password Reset
              </GhostButton>
            </div>
          )}
        </div>

        <div className="col-span-2 border-t border-[#27272A] pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="label-mute">Certifications</div>
            <button
              type="button"
              data-testid="add-certification-button"
              className="text-[10px] uppercase tracking-widest text-[#FF3B30] hover:text-[#FF453A]"
              onClick={addCert}
            >+ Add certification</button>
          </div>
          {(form.certifications || []).length === 0 && (
            <div className="text-[11px] text-zinc-500 italic">No certifications recorded for this .</div>
          )}
          <div className="space-y-2">
            {(form.certifications || []).map((c, idx) => (
              <div key={certKeys[idx] || `cert-idx-${idx}`} className="grid grid-cols-12 gap-2 items-end border border-[#27272A] p-2" data-testid={`certification-row-${idx}`}>
                <div className="col-span-4">
                  <div className="label-mute mb-1">Name</div>
                  <input
                    className={inputClass}
                    value={c.name || ""}
                    placeholder="e.g. CPR, Armed, K9"
                    data-testid={`cert-${idx}-name`}
                    onChange={(e) => updateCert(idx, { name: e.target.value })}
                  />
                </div>
                <div className="col-span-3">
                  <div className="label-mute mb-1">Issued</div>
                  <input
                    type="date"
                    className={inputClass}
                    value={c.issued_at || ""}
                    data-testid={`cert-${idx}-issued`}
                    onChange={(e) => updateCert(idx, { issued_at: e.target.value })}
                  />
                </div>
                <div className="col-span-3">
                  <div className="label-mute mb-1">Expires</div>
                  <input
                    type="date"
                    disabled={!!c.no_expiry}
                    className={`${inputClass} disabled:opacity-40`}
                    value={c.expires_at || ""}
                    data-testid={`cert-${idx}-expires`}
                    onChange={(e) => updateCert(idx, { expires_at: e.target.value })}
                  />
                </div>
                <div className="col-span-1 flex items-center pb-2">
                  <label className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400">
                    <input
                      type="checkbox"
                      data-testid={`cert-${idx}-no-expiry`}
                      checked={!!c.no_expiry}
                      onChange={(e) => updateCert(idx, { no_expiry: e.target.checked, expires_at: e.target.checked ? "" : c.expires_at })}
                    />
                    No expiry
                  </label>
                </div>
                <div className="col-span-1 flex items-center justify-end pb-2">
                  <button
                    type="button"
                    data-testid={`cert-${idx}-remove`}
                    onClick={() => removeCert(idx)}
                    className="text-zinc-500 hover:text-[#FF3B30] p-1"
                    aria-label="Remove certification"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-2">
          <FormField label="Notes">
            <textarea rows={3} className={inputClass} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </FormField>
        </div>

        {editing && <GuardRecentActivity guardId={editing.id} guardName={editing.name} />}
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <ActionButton data-testid="save-guard-button" onClick={onSave}>Save Officer</ActionButton>
      </div>
    </Modal>
  );
}
